import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { observer } from 'mobx-react'
import { useParams } from 'react-router-dom'
import { twMerge as mergeClassNames } from 'tailwind-merge'
import dayjs from 'dayjs'
import _ from 'lodash'
import { Controller, useForm } from 'react-hook-form'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

// Components
import { Button } from '../../components/Button'
import { CheckListIcon } from '../../components/CheckListIcon'
import { DatePicker } from '../../components/DatePicker'
import { EventHeader } from '../../components/EventHeader'
import { LocationIcon } from '../../components/LocationIcon'
import { RichTextInput } from '../../components/RichTextInput'
import { Select } from '../../components/Select'
import { StateContainer } from '../../components/StateContainer'
import { TextInput } from '../../components/TextInput'

// Local Components
import { Attendees } from './Attendees'
import { SessionOptions } from './SessionOptions'

// Images
import Calendar from '../../assets/images/calendar.svg'

// Store
import { NavigationStoreContext } from '../../stores/NavigationStore'
import { TaskStoreContext } from '../../stores/TaskStore'

// Service
import {
  getSession,
  updateSession,
  createSessionAttendeeExport,
} from '../../services/events.service'

// Utils & Styles
import { joinClassNames, toast } from '../../utils/helpers'

dayjs.extend(timezone)
dayjs.extend(utc)

const TABS = [
  {
    id: 0,
    label: 'Session Information',
  },
  {
    id: 1,
    label: 'Session Options',
  },
  {
    id: 2,
    label: 'Attendees',
  },
]

/**
 *
 * EventSessionDetail
 *
 */
const EventSessionDetail = observer(() => {
  // Context
  const { event, eventId } = useContext(NavigationStoreContext)
  const { setTask, setType, setParent } = useContext(TaskStoreContext)
  const { sessionId } = useParams()

  // State
  const [loadingSession, setLoadingSession] = useState(false)
  const [session, setSession] = useState(null)
  const [active, setActive] = useState(TABS[0])
  const [showDate, setShowDate] = useState(false)
  const [loadingAttendeeExport, setLoadingAttendeeExport] = useState(false)

  const handleSuccess = (m) => toast(m, 'success')
  const handleErrors = (m) => toast(m, 'error')

  const DEFAULT = {
    name: null,
    type: null,
    date: null,
    startsAt: null,
    endsAt: null,
    location: null,
    numOfDevices: null,
    sessionCode: null,
    creditType: null,
    creditAmount: null,
    scanningNotes: '<p><br></p>',
  }

  const {
    control,
    formState: { dirtyFields, errors },
    getValues,
    register,
    reset,
    setValue,
  } = useForm({
    mode: 'onBlur',
    shouldUnregister: false,
    defaultValues: DEFAULT,
  })

  /**
   * Gets the session.
   */
  const getUpdatedSession = async () => {
    const response = await getSession(
      eventId,
      sessionId,
      handleErrors,
      setLoadingSession,
      () => {},
    )

    if (response) {
      // Apply timezone after getting response since the service timezone is MT
      const date = dayjs(response.startsAt).tz(event.timezone)
      const endsAt = dayjs(response.endsAt).tz(event.timezone)

      const sessionData = {
        ...response,
        date: date.format('YYYY-MM-DD'),
        startsAt: date.format('HH:mm'),
        endsAt: endsAt.format('HH:mm'),
      }

      setSession(sessionData)
      reset(sessionData, { keepDefaultValues: false })
    }
  }

  /**
   * Loads session details and disable fields if registration sync is enabled.
   */
  useEffect(() => {
    getUpdatedSession()
  }, [])

  /**
   * Updates the session info.
   */
  const handleSubmit = async (data, dirty) => {
    // Pull out dirty fields so we only update what's necessary
    const dirtyData = _.pickBy(data, (value, key) => dirty[key])

    // If no fields are dirty, don't process request
    if (_.isEmpty(dirtyData)) return

    const updatedData = { ...dirtyData }

    // Load the date as in the event timezone
    const date = dayjs.tz(dayjs(data.date).format('YYYY-MM-DD'), event.timezone)

    // Only format and save dates that have changed
    if (dirtyData.date) {
      updatedData.startsAt = date
        .set('hour', data.startsAt.split(':')[0])
        .set('minute', data.startsAt.split(':')[1])
        .utc()
        .format()
      updatedData.endsAt = date
        .set('hour', data.endsAt.split(':')[0])
        .set('minute', data.endsAt.split(':')[1])
        .utc()
        .format()
    } else if (dirtyData.startsAt) {
      updatedData.startsAt = date
        .set('hour', data.startsAt.split(':')[0])
        .set('minute', data.startsAt.split(':')[1])
        .utc()
        .format()
    } else if (dirtyData.endsAt) {
      updatedData.endsAt = date
        .set('hour', data.endsAt.split(':')[0])
        .set('minute', data.endsAt.split(':')[1])
        .utc()
        .format()
    }

    const updatedSession = {
      ...updatedData,
      eventId,
      id: sessionId,
    }

    const response = await updateSession(updatedSession, handleErrors, () => {}, handleSuccess)

    if (response) {
      // Apply timezone after getting response since the service timezone is MT
      const updatedDate = dayjs(response.startsAt).tz(event.timezone)
      const endsAt = dayjs(response.endsAt).tz(event.timezone)

      const sessionData = {
        ...response,
        date: updatedDate.format('YYYY-MM-DD'),
        startsAt: updatedDate.format('HH:mm'),
        endsAt: endsAt.format('HH:mm'),
      }

      setSession(sessionData)
      reset(sessionData, { keepDefaultValues: false })
    }
  }

  const debounceHandleSubmit = useCallback(_.debounce(handleSubmit, 500), [])

  // Build external session options from event.sessions metadata
  const externalSessionOptions = useMemo(() => {
    if (!event?.sessions) return []
    return Object.entries(event.sessions).map(([id, s]) => ({
      id,
      label: s.name,
    }))
  }, [event?.sessions])

  // Resolve the currently linked external session for the Select value
  const currentExternalSession = useMemo(() => {
    if (!session?.externalId || !event?.sessions) return null
    const s = event.sessions[session.externalId]
    if (!s) return null
    return { id: session.externalId, label: s.name }
  }, [session?.externalId, event?.sessions])

  const renderTabContent = () => {
    switch (active.id) {
      case 0:
        return (
          <div className="flex h-full w-full flex-col gap-5 overflow-y-auto px-9">
            <div className="flex flex-col gap-4">
              <div className="flex justify-end">
                <div
                  role="group"
                  aria-label="Session type"
                  className="ml-4 flex items-center gap-2 rounded-xl bg-blue-800/10 p-1"
                >
                  <button
                    type="button"
                    aria-pressed={session.type === 'Access Control'}
                    aria-label="AC"
                    className={`rounded-lg px-2 py-0.5 ${
                      session.type === 'Access Control'
                        ? 'bg-status-green text-role-admin shadow-md'
                        : 'bg-transparent'
                    }`}
                    onClick={async () => {
                      const updatedSession = {
                        id: session.id,
                        eventId,
                        type: 'Access Control',
                      }
                      const response = await updateSession(
                        updatedSession,
                        handleErrors,
                        () => {},
                        handleSuccess,
                      )

                      if (response) {
                        setSession(response)
                      }
                    }}
                  >
                    AC
                  </button>
                  <button
                    type="button"
                    aria-pressed={session.type === 'Data Collection'}
                    aria-label="DC"
                    className={`rounded-lg px-2 py-0.5 ${
                      session.type === 'Data Collection'
                        ? 'bg-purple text-white shadow-md'
                        : 'bg-transparent'
                    }`}
                    onClick={async () => {
                      const updatedSession = {
                        id: session.id,
                        eventId,
                        type: 'Data Collection',
                      }
                      const response = await updateSession(
                        updatedSession,
                        handleErrors,
                        () => {},
                        handleSuccess,
                      )

                      if (response) {
                        setSession(response)
                      }
                    }}
                  >
                    DC
                  </button>
                </div>
              </div>
              <div className="flex flex-col gap-4 lg:flex-row">
                <TextInput
                  className="rounded-2xl border-gray-550 py-2.5 pl-9 pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
                  containerClassName="w-full"
                  data-testid="name"
                  disabled={loadingSession} // FIXME: should be disabled if this session is synced
                  error={errors.name && (errors.name?.message || 'This field is required')}
                  icon={<CheckListIcon className="ml-2 h-5 stroke-purple" aria-hidden="true" />}
                  id="name"
                  inputStyles="rounded-none rounded-t-md font-nunito"
                  label="Name"
                  name="name"
                  {...register('name', { required: true, shouldDirty: true })}
                  onChange={(e) => setValue('name', e.target.value, { shouldDirty: true })}
                  onBlur={() => debounceHandleSubmit(getValues(), dirtyFields)}
                />

                <Controller
                  name="date"
                  control={control}
                  render={({ field: { value } }) => (
                    <DatePicker
                      disabled={loadingSession} // FIXME: should be disabled if this session is synced
                      error={errors.date && (errors.date.message || 'This field is required')}
                      label="Date"
                      name="Date"
                      onChange={(e) => {
                        setValue('date', e, { shouldDirty: true })
                        debounceHandleSubmit(getValues(), dirtyFields)
                      }}
                      show={showDate}
                      setShow={setShowDate}
                      value={value}
                    />
                  )}
                  rules={{
                    required: true,
                  }}
                />

                <TextInput
                  className="rounded-2xl border-gray-550 py-2.5 pl-9 pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
                  containerClassName="w-full"
                  data-testid="startsAt"
                  disabled={loadingSession} // FIXME: should be disabled if this session is synced
                  icon={<img alt="Calendar" className="ml-2 h-5" src={Calendar} />}
                  id="startsAt"
                  inputStyles="rounded-none rounded-t-md font-nunito"
                  label="Start Time"
                  sublabel={`(${event.timezone})`}
                  name="startsAt"
                  type="time"
                  {...register('startsAt', { required: true, shouldDirty: true })}
                  onChange={(e) => setValue('startsAt', e.target.value, { shouldDirty: true })}
                  onBlur={() => debounceHandleSubmit(getValues(), dirtyFields)}
                />
              </div>

              <div className="flex flex-col gap-4 lg:flex-row">
                <TextInput
                  className="rounded-2xl border-gray-550 py-2.5 pl-9 pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
                  containerClassName="w-full"
                  data-testid="endsAt"
                  disabled={loadingSession} // FIXME: should be disabled if this session is synced
                  icon={<img alt="Calendar" className="ml-2 h-5" src={Calendar} />}
                  id="endsAt"
                  inputStyles="rounded-none rounded-t-md font-nunito"
                  label="End Time"
                  sublabel={`(${event.timezone})`}
                  name="endsAt"
                  type="time"
                  {...register('endsAt', { required: true, shouldDirty: true })}
                  onChange={(e) => setValue('endsAt', e.target.value, { shouldDirty: true })}
                  onBlur={() => debounceHandleSubmit(getValues(), dirtyFields)}
                />

                <TextInput
                  className="rounded-2xl border-gray-550 py-2.5 pl-9 pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
                  containerClassName="w-full"
                  data-testid="location"
                  disabled={loadingSession}
                  icon={<LocationIcon className="ml-2 h-5 stroke-purple" aria-hidden="true" />}
                  id="location"
                  inputStyles="rounded-none rounded-t-md font-nunito"
                  label="Location"
                  name="location"
                  placeholder="Location"
                  {...register('location', { required: true, shouldDirty: true })}
                  onChange={(e) => setValue('location', e.target.value, { shouldDirty: true })}
                  onBlur={() => debounceHandleSubmit(getValues(), dirtyFields)}
                />
              </div>
            </div>

            <div className="flex w-full flex-col gap-4">
              <div className="text-lg font-bold">Optional Session Info</div>

              {externalSessionOptions.length > 0 && (
                <Select
                  className="w-full rounded-2xl border-gray-550 px-4 py-2.5 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
                  disabled={loadingSession}
                  fullWidth
                  inputStyles="rounded-none rounded-t-md font-nunito"
                  label="Linked External Session"
                  name="externalId"
                  onChange={async (selected) => {
                    const updatedSession = {
                      id: session.id,
                      eventId,
                      externalId: selected?.id || null,
                    }
                    const response = await updateSession(
                      updatedSession,
                      handleErrors,
                      () => {},
                      handleSuccess,
                    )
                    if (response) {
                      setSession((prev) => ({ ...prev, externalId: selected?.id || null }))
                    }
                  }}
                  options={externalSessionOptions}
                  placeholder="Select an external session (optional)"
                  style={{ width: '100%' }}
                  value={currentExternalSession}
                />
              )}

              <div className="flex flex-col items-center gap-4 lg:flex-row">
                <TextInput
                  className="rounded-2xl border-gray-550 py-2.5 pl-2 pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
                  containerClassName="w-full"
                  data-testid="numOfDevices"
                  disabled={loadingSession}
                  id="numOfDevices"
                  inputStyles="rounded-none rounded-t-md font-nunito"
                  label="Number of Devices"
                  name="numOfDevices"
                  type="number"
                  placeholder="Number of Devices"
                  {...register('numOfDevices', { shouldDirty: true })}
                  onChange={(e) => setValue('numOfDevices', e.target.value, { shouldDirty: true })}
                  onBlur={() => debounceHandleSubmit(getValues(), dirtyFields)}
                />

                <TextInput
                  className="ph-9 rounded-2xl border-gray-550 py-2.5 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
                  containerClassName="w-full"
                  data-testid="capacity"
                  disabled={loadingSession}
                  id="capacity"
                  inputStyles="rounded-none rounded-t-md font-nunito"
                  label="Session Capacity"
                  name="capacity"
                  placeholder="Session Capacity"
                  type="number"
                  {...register('capacity', { shouldDirty: true })}
                  onChange={(e) => setValue('capacity', e.target.value, { shouldDirty: true })}
                  onBlur={() => debounceHandleSubmit(getValues(), dirtyFields)}
                />

                <TextInput
                  className="ph-9 rounded-2xl border-gray-550 py-2.5 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
                  containerClassName="w-full"
                  data-testid="sessionCode"
                  disabled={loadingSession}
                  id="sessionCode"
                  inputStyles="rounded-none rounded-t-md font-nunito"
                  label="Session Code"
                  name="sessionCode"
                  placeholder="Session Code"
                  {...register('sessionCode', { shouldDirty: true })}
                  onChange={(e) => setValue('sessionCode', e.target.value, { shouldDirty: true })}
                  onBlur={() => debounceHandleSubmit(getValues(), dirtyFields)}
                />
              </div>

              <div className="flex space-x-2">
                <TextInput
                  className="ph-9 rounded-2xl border-gray-550 py-2.5 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
                  containerClassName="w-full"
                  data-testid="track"
                  disabled={loadingSession}
                  id="track"
                  inputStyles="rounded-none rounded-t-md font-nunito"
                  label="Track"
                  name="track"
                  placeholder="Track"
                  {...register('track', { shouldDirty: true })}
                  onChange={(e) => setValue('track', e.target.value, { shouldDirty: true })}
                  onBlur={() => debounceHandleSubmit(getValues(), dirtyFields)}
                />

                <TextInput
                  className="ph-9 rounded-2xl border-gray-550 py-2.5 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
                  containerClassName="w-full"
                  data-testid="creditType"
                  disabled={loadingSession}
                  id="creditType"
                  inputStyles="rounded-none rounded-t-md font-nunito"
                  label="Credit Type"
                  name="creditType"
                  placeholder="Credit Type"
                  {...register('creditType', { shouldDirty: true })}
                  onChange={(e) => setValue('creditType', e.target.value, { shouldDirty: true })}
                  onBlur={() => debounceHandleSubmit(getValues(), dirtyFields)}
                />

                <TextInput
                  className="ph-9 rounded-2xl border-gray-550 py-2.5 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
                  containerClassName="w-full"
                  data-testid="creditAmount"
                  disabled={loadingSession}
                  id="creditAmount"
                  inputStyles="rounded-none rounded-t-md font-nunito"
                  label="Credit Amount"
                  name="creditAmount"
                  placeholder="Credit Amount"
                  type="number"
                  {...register('creditAmount', { shouldDirty: true })}
                  onChange={(e) => setValue('creditAmount', e.target.value, { shouldDirty: true })}
                  onBlur={() => debounceHandleSubmit(getValues(), dirtyFields)}
                />
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium text-gray-700">Scanning Notes</span>
              <RichTextInput
                className="rounded-lg border-gray-550 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
                disabled={loadingSession}
                defaultValue="<p><br></p>"
                id="scanningNotes"
                onChange={(value) => {
                  setValue('scanningNotes', value, { shouldDirty: true })
                  debounceHandleSubmit(getValues(), dirtyFields)
                }}
                placeholder="Add scanning notes here"
                toolbarStyles="border-gray-550"
                value={session.scanningNotes || '<p><br></p>'}
              />
            </div>
          </div>
        )
      case 1:
        return (
          <SessionOptions
            event={event}
            loading={loadingSession}
            session={session}
            setSession={setSession}
          />
        )
      case 2:
        return <Attendees session={session} timezone={event.timezone} />
      default:
        return null
    }
  }

  if (!session) {
    return null
  }

  return (
    <div className="h-full w-full">
      <StateContainer loading={loadingSession}>
        <div className="flex h-full w-full flex-col overflow-y-auto p-3">
          <div className="flex flex-col gap-6">
            <div className="flex flex-col items-start justify-between gap-2 px-3 sm:flex-row md:items-center">
              <EventHeader event={event} />

              <Button
                label="Export Attendees"
                loading={loadingAttendeeExport}
                onClick={async () => {
                  const task = await createSessionAttendeeExport(
                    event.id,
                    session.id,
                    handleErrors,
                    setLoadingAttendeeExport,
                  )

                  if (task) {
                    setType('Download')
                    setParent({
                      type: 'session-attendee-export',
                      label: 'Session Attendees',
                      id: task.id,
                      eventId: event.id,
                      sessionId: session.id,
                      eventName: session.name,
                      fileName: `[${session.type}] ${session.trackrId} - ${dayjs(
                        session.date,
                      ).format('YYYY-MM-DD')}`,
                    })
                    setTask(task)
                  }
                }}
              />
            </div>

            <div className="flex flex-col items-center justify-center gap-3 px-3 xl:flex-row">
              <span className="text-center text-2xl font-bold">{session.name}</span>

              <div className="flex flex-none flex-row items-center gap-3">
                <span
                  className={mergeClassNames(
                    'flex items-center rounded-full px-2.5 py-2 text-xs font-bold text-white shadow-sm',
                    session.type === 'Access Control'
                      ? 'bg-status-green text-role-admin'
                      : 'bg-purple',
                  )}
                >
                  {session.type}
                </span>

                <span className="text-sm font-semibold">Trackr ID: {session.trackrId}</span>
              </div>
            </div>

            <div className="px-3">
              <nav className="flex" aria-label="Tabs">
                {TABS.map((tab) => {
                  const activeTab = active.id === tab.id

                  return (
                    <button
                      type="button"
                      key={tab.label}
                      onClick={() => setActive(tab)}
                      className={joinClassNames(
                        activeTab
                          ? 'border-purple-700 bg-[#8c56f626] text-purple-700'
                          : 'border-b-transparent text-gray-600 hover:border-purple-700 hover:bg-[#8c56f626] hover:text-purple-700',
                        'group relative flex w-1/3 items-center justify-center rounded-t-[10px] border-b-2 px-4 py-2 text-sm font-medium',
                      )}
                      aria-current={activeTab ? 'page' : undefined}
                    >
                      {tab.label}
                    </button>
                  )
                })}
              </nav>
            </div>
          </div>

          <div className="shadow-xm flex h-[70%] w-full flex-col overflow-hidden rounded-[15px] bg-white px-5 py-4 tall:h-auto">
            {renderTabContent()}
          </div>
        </div>
      </StateContainer>
    </div>
  )
})

export default EventSessionDetail
